import { Stage } from '../../../types/pipeline';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useUpdateStageMutation } from '../../../api/pipelineApi';
import { CircularProgress } from '@mui/joy';
import { isFetchBaseQueryError, parseErrorToDetailProblem } from '../../../utils/errorHelpers';
import { ErrorMessage } from '../../UtilitiesComponents/ErrorMessage';
import Switch from '@mui/joy/Switch';

interface Props {
    stage: Stage;
}

export function EditStages({ stage }: Props) {
    const {
        control,
        register,
        handleSubmit,
        reset,
        watch,
        formState: {
            isDirty
        }
    } = useForm<Stage>({ 
        defaultValues: stage
    })

    const enableRottingIn = watch("enableRottingIn");

    const [updateStage, { isLoading }] = useUpdateStageMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [stageName, setStageName] = useState(watch("nombre"));

    const submitUpdate = handleSubmit(async (data: Stage) => {
        try {
            resetErrorMessage();
            await updateStage(data).unwrap();
            reset(data);
            setStageName(data.nombre);
        } catch (error) {
            if(isFetchBaseQueryError(error)) {
                const problemDetails = parseErrorToDetailProblem(error);
                if(problemDetails)
                    setErrorMessage(problemDetails.title);
            }
        }
    });

    const resetErrorMessage = () => setErrorMessage(null);

    return (
        <div className="border-0 border-blue-800 flex flex-col bg-slate-200 rounded">
            <form onSubmit={submitUpdate} autoComplete="off">
                <div className="border-b-2 border-b-gray-300 border-black py-2 h-[100px]">
                    <div className="mx-2">
                        <p className="text-l font-semibold">{stageName}</p>
                        {isDirty && (
                        <>
                        <div className="flex gap-x-2 justify-center py-2">
                        {isLoading
                            ? 
                            <CircularProgress sx={{ 
                                height: 27,
                                width: 27,
                                "--CircularProgress-size": "25px",
                                "--CircularProgress-trackThickness": "4px",
                                "--CircularProgress-progressThickness": "4px"
                            }}/>
                            :
                            <>
                            <button 
                                type="submit"
                                className="bg-green-800 rounded-[3px] px-[6px] py-[2px] flex gap-1 items-center">
                                <p className="text-white text-[13px] font-semibold text-center">Guardar</p>
                            </button>
                            <button 
                                onClick={() => {
                                    reset();
                                    resetErrorMessage();
                                }}
                                className="border-2 border-gray-600 rounded-[3px] px-[6px] py-[2px] flex gap-1 items-center">
                                <p className="text-gray-600 text-[13px] font-semibold text-center">Cancelar</p>
                            </button>
                            </>
                        }

                        </div>

                        {errorMessage && (
                            <ErrorMessage 
                            customMessage>
                                <p className="text-sm">{errorMessage}</p>
                            </ErrorMessage>)}
                        </>)}
                    </div>
                </div>
                <div className="w-full h-full py-3 px-2">
                    <div className="w-full grid gap-3">
                        <div>
                            <label className="text-sm font-semibold">Nombre</label>
                            <input 
                                {...register("nombre")}
                                className="
                                    w-full h-[26px] rounded-[3px] border-gray-400 border my-1 px-2 
                                    font-semibold text-gray-600 text-sm"
                                type="text" />
                        </div>

                        <div>
                            <div className="flex gap-x-2">
                                <Controller
                                    name="enableRottingIn"
                                    control={control}
                                    render={({ field }) => (
                                    <Switch
                                        sx={{
                                            "--Switch-trackHeight": "16px",
                                            "--Switch-thumbSize": "11px"
                                        }}
                                        checked={field.value}
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        />
                                    )}/>
                                <p className="text-sm font-semibold">Vencido en (días)</p>
                            </div>
                            {enableRottingIn && (
                                <input 
                                    {...register("rottingInDays")}
                                    className="
                                        w-full h-[26px] rounded-[3px] border-gray-400 border my-1 px-2 
                                        font-semibold text-gray-600 text-sm"
                                    type="number" />
                            )}
                        </div>
                    </div>
                    
                </div>
            </form>

        </div>
    );
}